const RETRY_CAPABILITY = Symbol("grocery-checkoff-retry");

export interface RetryableDirectCheckoffError extends Error {
  readonly status: number | undefined;
  readonly checkoffEpoch: string;
  readonly operationId: string;
  readonly cause: unknown;
  readonly [RETRY_CAPABILITY]: true;
}

export interface DirectCheckoffRetryBinding {
  groceryListId: string;
  itemId: string;
  checked: boolean;
  checkoffEpoch: string;
  operationId: string;
}

type RetryCapabilityRecord = DirectCheckoffRetryBinding & {
  queuedAt?: number;
};

let issuedCapabilities = new WeakSet<object>();
let capabilityRecords = new WeakMap<object, RetryCapabilityRecord>();

class RetryableDirectCheckoffErrorImpl
  extends Error
  implements RetryableDirectCheckoffError
{
  readonly status: number | undefined;
  readonly checkoffEpoch: string;
  readonly operationId: string;
  readonly cause: unknown;
  readonly [RETRY_CAPABILITY] = true as const;

  constructor(cause: unknown, binding: DirectCheckoffRetryBinding) {
    super(cause instanceof Error ? cause.message : "Grocery checkoff failed");
    this.name = "RetryableDirectCheckoffError";
    this.status = errorStatus(cause);
    this.checkoffEpoch = binding.checkoffEpoch;
    this.operationId = binding.operationId;
    this.cause = cause;
  }
}

/** Issued only where a direct request actually crosses the API boundary. */
export function issueRetryableDirectCheckoffError(
  cause: unknown,
  binding: DirectCheckoffRetryBinding,
): RetryableDirectCheckoffError {
  const capability = new RetryableDirectCheckoffErrorImpl(cause, binding);
  issuedCapabilities.add(capability);
  capabilityRecords.set(capability, { ...binding });
  return capability;
}

export function isRetryableDirectCheckoffError(
  error: unknown,
): error is RetryableDirectCheckoffError {
  try {
    return (
      typeof error === "object" &&
      error !== null &&
      issuedCapabilities.has(error)
    );
  } catch {
    return false;
  }
}

/**
 * Consumes an authentic capability only for its exact product target. The
 * first successful consumer fixes queuedAt for every sibling hook participant.
 */
export function consumeRetryableDirectCheckoffError(
  error: unknown,
  expected: Pick<
    DirectCheckoffRetryBinding,
    "groceryListId" | "itemId" | "checked"
  >,
): (Pick<DirectCheckoffRetryBinding, "checkoffEpoch" | "operationId"> & {
  queuedAt: number;
}) | undefined {
  if (!isRetryableDirectCheckoffError(error)) return undefined;
  try {
    const record = capabilityRecords.get(error);
    if (
      !record ||
      record.groceryListId !== expected.groceryListId ||
      record.itemId !== expected.itemId ||
      record.checked !== expected.checked
    ) {
      return undefined;
    }
    record.queuedAt ??= Date.now();
    return {
      checkoffEpoch: record.checkoffEpoch,
      operationId: record.operationId,
      queuedAt: record.queuedAt,
    };
  } catch {
    return undefined;
  }
}

/** Test isolation for opaque capability registries. */
export function resetRetryableDirectCheckoffErrorsForTests(): void {
  issuedCapabilities = new WeakSet<object>();
  capabilityRecords = new WeakMap<object, RetryCapabilityRecord>();
}

function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}
